import { EditorElementBuilder } from '@wix/component-protocol/builder';
import {
  InlineElement,
  States,
  type EditorReactComponent,
} from '@wix/component-protocol/schema';
import {
  ActionType,
  type CustomAction,
  type CustomActions,
  type DashboardAction,
  type ElementParam,
  type InnerElement,
  PanelContentType,
  PanelSizeWidth,
  PanelType,
  type ResolvedOOIWidgetStatics,
  type SettingsAction,
  type DisplayFilters,
  type EditorElementLayout,
  DefaultBehaviors,
  DefaultLayout,
  StyleType,
} from '@wix/editor-platform-ooi-metadata-resolver';
import {
  mapOOIDisplayFiltersToManifestDisplayFilters,
  mapOOISettingsToManifestData,
  mapOOIStylesToManifestStyles,
  mapOOIPresetsToManifestPresets,
  mapOOIGroupingsToManifestDisplayGroups,
  mapOOIInstallationToManifestInstallation,
  type MapperOptions,
  type PresetsParams,
  type InstallationInfo,
  type SettingParams,
  type StyleParams,
  type DisplayGroupsParams,
} from '@wix/editor-platform-ooi-metadata-resolver/mappers';

interface Options {
  statics: Partial<ResolvedOOIWidgetStatics>;
  nativeSettingsPanelUrl: string | null;
  layout: EditorElementLayout | null;
}

type InnerElementBuilderDefinition = Pick<
  NonNullable<InlineElement>,
  'data' | 'cssCustomProperties' | 'displayGroups'
>;

export class OOIManifestBuilder {
  private readonly builder: EditorElementBuilder;
  #displayName: string | undefined;
  #styleParams: StyleParams | undefined;
  #settingParams: SettingParams | undefined;
  #dashboardAction: DashboardAction | undefined;
  #settingsAction: SettingsAction | undefined;
  #customActions: CustomActions | undefined;
  #nativeSettingsPanelUrl: Options['nativeSettingsPanelUrl'];
  #innerElements: InnerElement<any, any>[];
  #displayFilters: DisplayFilters | undefined;
  #states: States | undefined;
  #presets: PresetsParams | undefined;
  #installation: InstallationInfo | undefined;
  #displayGroups: DisplayGroupsParams | undefined;
  #layout: Options['layout'];

  constructor({ statics: options, nativeSettingsPanelUrl, layout }: Options) {
    this.builder = new EditorElementBuilder();
    this.#displayName = options.displayName;
    this.#styleParams = options.styleParams;
    this.#settingParams = options.settingsParams;
    this.#dashboardAction = options.dashboardAction;
    this.#settingsAction = options.settingsAction;
    this.#customActions = options.customActions;
    this.#innerElements = options.innerElements ?? [];
    this.#displayFilters = options.displayFilters;
    this.#states = options.states;
    this.#presets = options.presetsPanel;
    this.#installation = options.installation;
    this.#displayGroups = options.groupings;
    this.#nativeSettingsPanelUrl = nativeSettingsPanelUrl;
    this.#layout = layout;
  }

  configure(fn: (builder: EditorElementBuilder) => void) {
    fn(this.builder);
    return this;
  }

  // when isMobile in not passed, we will create a style/data
  // params without default values to persist it in store
  build(options?: { isMobile?: boolean }): {
    editorElement: ReturnType<EditorElementBuilder['build']>;
    installation?: EditorReactComponent['installation'];
  } {
    const paramsData: Record<
      string,
      ElementParam<any, any>
    > = Object.fromEntries(
      this.#innerElements
        .flatMap(({ elementParams }) => elementParams || [])
        .filter((param) => param.displayName)
        .map((param) => [param.key, param]),
    );

    const mapperOptions: MapperOptions = {
      isMobile: options?.isMobile,
      paramsData,
    };

    const mappedStyleItems = this.#styleParams
      ? mapOOIStylesToManifestStyles(this.#styleParams, mapperOptions)
      : {};

    const mappedDataItems = this.#settingParams
      ? mapOOISettingsToManifestData(this.#settingParams, mapperOptions)
      : {};

    const mappedPresets = this.#presets
      ? mapOOIPresetsToManifestPresets(this.#presets, this.#innerElements)
      : {};

    const mappedDisplayGroups = this.#displayGroups
      ? mapOOIGroupingsToManifestDisplayGroups(this.#displayGroups)
      : {};

    const mappedInstallation = this.#installation
      ? mapOOIInstallationToManifestInstallation(this.#installation)
      : undefined;

    this.#innerElements.forEach((innerElement) => {
      const data: InnerElementBuilderDefinition['data'] = {};
      const style: InnerElementBuilderDefinition['cssCustomProperties'] = {};
      const displayGroups: InnerElementBuilderDefinition['displayGroups'] =
        innerElement.groupings
          ? mapOOIGroupingsToManifestDisplayGroups(innerElement.groupings)
          : {};

      for (const param of innerElement.elementParams ?? []) {
        const key = param.key as string;

        if (key in mappedStyleItems) {
          if (
            this.#styleParams?.[key].metadata?.type === StyleType.booleanValue
          ) {
            continue;
          }

          style[key] = Object.assign(
            param.displayName ? { displayName: param.displayName } : {},
            mappedStyleItems[key],
          );

          // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
          delete mappedStyleItems[key];
        } else {
          data[key] = Object.assign(
            param.displayName ? { displayName: param.displayName } : {},
            mappedDataItems[key],
          );

          // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
          delete mappedDataItems[key];
        }
      }

      this.builder.setInlineElement(innerElement.id, {
        displayName: innerElement.displayName,
        data,
        cssCustomProperties: style,
        displayGroups,
        behaviors: DefaultBehaviors,
      });
    });

    if (Object.keys(mappedStyleItems).length) {
      this.builder.setCssCustomProperties(mappedStyleItems);
    }

    if (Object.keys(mappedDataItems).length) {
      this.builder.setData(mappedDataItems);
    }

    if (Object.keys(mappedPresets).length) {
      this.builder.setPresets(mappedPresets);
    }

    if (Object.keys(mappedDisplayGroups).length) {
      this.builder.setDisplayGroups(mappedDisplayGroups);
    }

    if (this.#states) {
      this.builder.setStates(this.#states);
    }

    if (this.#dashboardAction) {
      this.builder.setAction('dashboard', this.#dashboardAction);
    }

    if (this.#nativeSettingsPanelUrl) {
      this.builder.setAction('settings', {
        displayName: this.#settingsAction?.displayName,
        execution: {
          actionType: ActionType.panel,
          panel: {
            panelType: PanelType.inline,
            inline: {
              contentType: PanelContentType.url,
              url: this.#nativeSettingsPanelUrl,
              size: {
                width: PanelSizeWidth.MEDIUM,
                height: 528,
              },
            },
          },
        },
      });
    }

    this.builder.setLayout(this.#layout || DefaultLayout);

    if (this.#displayName) {
      this.builder.setDisplayName(this.#displayName);
    }

    this.builder.setDisplayFilters(
      mapOOIDisplayFiltersToManifestDisplayFilters(this.#displayFilters),
    );

    this.#customActions &&
      this.builder.setCustomActions(
        this.#applyCustomActionDefinitionFixer(this.#customActions),
      );

    const manifest = this.builder.build();

    return {
      editorElement: manifest,
      ...(mappedInstallation ? { installation: mappedInstallation } : {}),
    };
  }

  #applyCustomActionDefinitionFixer(
    customActions: CustomActions,
  ): CustomActions {
    const customActionsEntries = Object.entries(customActions).map<
      [string, CustomAction]
    >(([id, definition]) => {
      return [
        id,
        {
          ...definition,
          execution: {
            ...definition.execution,
            actionType:
              definition.execution.actionType ||
              this.#getCustomActionType(definition.execution),
          },
        },
      ];
    });

    return Object.fromEntries(customActionsEntries);
  }

  #getCustomActionType(execution: CustomAction['execution']): ActionType {
    if ('data' in execution) {
      return ActionType.data;
    }

    if ('event' in execution) {
      return ActionType.event;
    }

    if ('forward' in execution) {
      return ActionType.forward;
    }

    if ('panel' in execution) {
      return ActionType.panel;
    }

    if ('style' in execution) {
      return ActionType.style;
    }

    return ActionType.UNKNOWN_ActionType;
  }
}
